import React, { useContext } from 'react'
import { useState } from 'react'
import ProfileSection from 'app/common/pixel-menu/_comp/ProfileSection'
import ProductDetail from 'client/modules/inventory/pages/product/components/ProductDetail'
import { get, put } from 'client/utils/api-routes.util'
import ArrowRightFromBracketIcon from 'client/utils/icons/arrow-right-from-bracket-icon'
import CalendarIcon from 'client/utils/icons/calendar-icon'
import CheckIcon from 'client/utils/icons/check-icon'
import MarketplaceStallIcon from 'client/utils/icons/marketplace-stall-icon'
import PlusIcon from 'client/utils/icons/plus-icon'
import ProductCatalogIcon from 'client/utils/icons/product-catalog-icon'
import ReferenceIcon from 'client/utils/icons/reference-icon'
import StickyNoteIcon from 'client/utils/icons/sticky-note-icon'
import TrashIcon from 'client/utils/icons/trash-icon'
import { WarehouseIcon } from 'client/utils/icons/warehouse-icon'
import XmarkIcon from 'client/utils/icons/xmark-icon'
import {
  DcBreadcrumbs,
  DcButton,
  DcFlexBox,
  DcOverlaySlider,
  PixelPageContainer,
  PixelTable,
  PixelText,
  Section,
  SectionContent,
  colors,
  DcTag,
  DcDropDown,
  DcDiv,
  PixelDate,
  PixelFactoryContext,
  DcInput,
} from 'pixel-base'
import * as XLSX from 'xlsx'

import InventoryRequestForm from './InventoryRequestForm'
import PencilIcon from '../../../utils/icons/pencil-icon'

// New type for line item
interface IInventoryRequestLineItem {
  id: number
  ems_customer_id: number
  quantity: number
  created_at: {
    date: string
    timezone_type: number
    timezone: string
  }
  updated_at: string | null
  product: {
    product_id: number
    product_image_path?: string
    product_name: string
    product_sku: string
    quantity_available?: number | null
    product_price?: number
    sale_price?: number
    created_at: string
    permalink?: string
  }
  request: IInventoryRequest
}

// Detail Content Component
interface InventoryRequestDetailContentProps {
  lineItems: IInventoryRequestLineItem[]
  request: IInventoryRequest
  updateStatus: (requestId: number, status: string) => void
  reloadDetails: (requestId: number) => void
  Close: () => void
}

const InventoryRequestDetailContent: React.FC<
  InventoryRequestDetailContentProps
> = ({ lineItems, request, updateStatus, reloadDetails, Close }) => {
  const {
    pixelConfirm,
    showPageLoader,
    hidePageLoader,
    showSuccess,
    showFailure,
  } = useContext(PixelFactoryContext)
  const [showProductDetail, setShowProductDetail] = useState(false)
  const [data, setData] = useState<
    | {
        quantity: number
        product_id: number
      }[]
    | null
  >(null)
  const [edit, setEdit] = useState(false)
  const [selectedProductId, setSelectedProductId] = useState<number | null>(
    null,
  )
  const [currentStatus, setCurrentStatus] = useState<string>(
    request?.request_status || 'draft',
  )
  const [productSearch, setProductSearch] = useState<string>('')

  if (!lineItems || lineItems.length === 0) {
    return (
      <>
        <PixelPageContainer
          style={{
            minHeight: 'calc(100vh - 65px)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
          padding="20px"
        >
          <SectionContent style={{ padding: '20px', borderRadius: '0px' }}>
            <DcFlexBox
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              style={{
                minHeight: '400px',
                textAlign: 'center',
                padding: '40px 20px',
              }}
            >
              <DcDiv
                style={{
                  width: '120px',
                  height: '120px',
                  borderRadius: '50%',
                  backgroundColor: colors.grayLight,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '24px',
                }}
              >
                <ProductCatalogIcon size={48} color={colors.grayText} />
              </DcDiv>
              <PixelText
                style={{
                  fontSize: '24px',
                  fontWeight: '600',
                  color: colors.text,
                  marginBottom: '8px',
                }}
              >
                No Transfer Items Found
              </PixelText>
              <PixelText
                style={{
                  fontSize: '16px',
                  color: colors.grayText,
                  marginBottom: '24px',
                  maxWidth: '400px',
                  lineHeight: '1.5',
                }}
              >
                This warehouse transfer request doesn&apos;t have any items yet.
                Items will appear here once they are added to the transfer
                request.
              </PixelText>
              <DcFlexBox gap="12px" flexWrap="wrap" justifyContent="center">
                <DcButton
                  size="sm"
                  variant="outline-primary"
                  onClick={() => Close()}
                  withIcon={true}
                >
                  <ArrowRightFromBracketIcon size={16} />
                  Go Back
                </DcButton>
                <DcButton
                  size="sm"
                  variant="primary"
                  onClick={() => reloadDetails(request.id)}
                  withIcon={true}
                >
                  <CheckIcon size={16} />
                  Refresh Page
                </DcButton>
              </DcFlexBox>
            </DcFlexBox>
          </SectionContent>
        </PixelPageContainer>
      </>
    )
  }

  const handleApprove = (requestId: number) => {
    pixelConfirm({
      title: 'Approve Transfer Request',
      message:
        'Are you sure you want to approve this warehouse transfer request?',
      onConfirm: () => {
        showPageLoader()
        put(`/warehouse/transfer/${requestId}/status`, {
          status: 'completed',
        }).subscribe(
          (res: any) => {
            hidePageLoader()
            if (res.response.success || res.response.message) {
              updateStatus(requestId, 'completed')

              setCurrentStatus('completed')
              showSuccess(
                res.response.message ||
                  'Inventory request status updated successfully',
              )
            } else if (res.response.error) {
              // Handle validation errors
              const errorMessage = res.response.error
              const details = res.response.details

              if (details && details.length > 0) {
                // Build detailed error message
                let detailedErrors = `${errorMessage}: `
                const errorList = details.map((detail: any) => {
                  const productErrors = detail.errors.join(', ')
                  return `Product ID ${detail.product_id}: ${productErrors}`
                })
                detailedErrors += errorList.join('; ')
                console.error('Validation errors:', details)
                showFailure(detailedErrors)
              } else {
                showFailure(errorMessage || 'Failed to approve request')
              }
            }
          },
          (_error: any) => {
            hidePageLoader()
            showFailure('An unexpected error occurred. Please try again.')
          },
        )
      },
    })
  }

  const handleReject = (requestId: number) => {
    pixelConfirm({
      title: 'Reject Transfer Request',
      message:
        'Are you sure you want to reject this warehouse transfer request?',
      onConfirm: () => {
        showPageLoader()
        put(`/warehouse/transfer/${requestId}/status`, {
          status: 'cancelled',
        }).subscribe(
          (res: any) => {
            hidePageLoader()
            if (res.response.success || res.response.message) {
              updateStatus(requestId, 'cancelled')
              setCurrentStatus('cancelled')
              showSuccess(
                res.response.message ||
                  'Inventory request status updated successfully',
              )
            } else if (res.response.error) {
              // Handle validation errors
              const errorMessage = res.response.error
              const details = res.response.details

              if (details && details.length > 0) {
                // Build detailed error message
                let detailedErrors = `${errorMessage}: `
                const errorList = details.map((detail: any) => {
                  const productErrors = detail.errors.join(', ')
                  return `Product ID ${detail.product_id}: ${productErrors}`
                })
                detailedErrors += errorList.join('; ')
                console.error('Validation errors:', details)
                showFailure(detailedErrors)
              } else {
                showFailure(errorMessage || 'Failed to reject request')
              }
            }
          },
          (_error: any) => {
            hidePageLoader()
            showFailure('An unexpected error occurred. Please try again.')
          },
        )
      },
    })
  }
  const handleDeleteProduct = (productId: number) => {
    pixelConfirm({
      title: 'Delete Product',
      message: 'Are you sure you want to delete this product?',
      onConfirm: () => {
        // showPageLoader()
        //just remove the deleted products from the data array and update the server by sending the new data array
        const currentData =
          data && data.length > 0
            ? data
            : (lineItems || []).map((li) => ({
                product_id: li.product.product_id,
                quantity: li.quantity,
              }))
        const newData = currentData.filter(
          (item) => item.product_id !== productId,
        ) as { quantity: number; product_id: number }[]
        setData(newData)
      },
    })
  }

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return { bg: colors.successLight, text: colors.success }
      case 'pending':
        return { bg: colors.warningLight, text: colors.warning }
      case 'draft':
        return { bg: colors.infoLight, text: colors.info }
      case 'cancelled':
        return { bg: colors.dangerLight, text: colors.danger }
      default:
        return { bg: colors.grayLight, text: colors.grayText }
    }
  }
  const formatDate = (dateString: string) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleString()
  }
  const statusColors = getStatusColor(currentStatus)

  const handleEditProducts = () => {
    if (!request?.id) return
    // If in edit mode, always use `data` (even if empty) so deletions persist to server
    const productsList = edit
      ? data || []
      : (lineItems || []).map((item) => ({
          product_id: item.product.product_id,
          quantity: item.quantity,
        }))

    const payload = {
      products: productsList as { product_id: number; quantity: number }[],
    }
    showPageLoader()
    put(`/warehouse/transfer/${request.id}`, payload).subscribe(
      (res: any) => {
        hidePageLoader()
        if (res?.response?.success || res?.response?.message) {
          showSuccess(
            res.response.message || 'Inventory transfer updated successfully',
          )
          setEdit(false)
          setData(null)

          reloadDetails(request.id)
        } else {
          const errorMessage =
            res?.response?.error || 'Failed to update transfer'
          showFailure(errorMessage)
        }
      },
      () => {
        hidePageLoader()
        showFailure('An unexpected error occurred. Please try again.')
      },
    )
  }

  // PixelTable columns for products (without Link column)
  const columns = [
    {
      dataKey: 'product_image_path',
      name: <PixelText>Image</PixelText>,
      width: '80px',
      selector: (row: IInventoryRequestLineItem) =>
        row.product.product_image_path ? (
          <img
            src={row.product.product_image_path}
            style={{
              width: 50,
              height: 50,
              borderRadius: 4,
              objectFit: 'cover',
            }}
          />
        ) : (
          <span>-</span>
        ),
    },
    {
      dataKey: 'product_name',
      name: <PixelText>Name</PixelText>,
      selector: (row: IInventoryRequestLineItem) => row.product.product_name,
    },
    {
      dataKey: 'product_sku',
      name: <PixelText>SKU</PixelText>,
      selector: (row: IInventoryRequestLineItem) => row.product.product_sku,
    },
    {
      dataKey: 'quantity',
      name: <PixelText>Quantity</PixelText>,
      selector: (row: IInventoryRequestLineItem) => {
        return edit ? (
          <DcDiv width="100px">
            <DcInput
              value={
                (data?.find(
                  (item) => item.product_id === row.product.product_id,
                )?.quantity ?? row.quantity) as number
              }
              onChange={(e) => {
                const nextQuantity = Number(e.target.value)
                setData((prev) => {
                  const prevList = prev ? [...prev] : []
                  const idx = prevList.findIndex(
                    (i) => i.product_id === row.product.product_id,
                  )
                  if (idx >= 0) {
                    prevList[idx] = {
                      product_id: row.product.product_id,
                      quantity: nextQuantity,
                    }
                    return prevList
                  }
                  return [
                    ...prevList,
                    {
                      product_id: row.product.product_id,
                      quantity: nextQuantity,
                    },
                  ]
                })
              }}
              name={`quantity-${row.product.product_id}`}
            />
          </DcDiv>
        ) : (
          <PixelText>{row.quantity}</PixelText>
        )
      },
    },
    {
      dataKey: 'sale_price',
      width: 'fit-content',
      name: <PixelText>Price</PixelText>,
      selector: (row: IInventoryRequestLineItem) =>
        row.product.sale_price ? row.product.sale_price : '-',
    },
    {
      dataKey: 'delete',
      width: 'fit-content',
      name: <PixelText>actions</PixelText>,
      selector: (row: IInventoryRequestLineItem) =>
        edit ? (
          <DcButton
            size="sm"
            variant="outline-danger"
            onClick={() => handleDeleteProduct(row.product.product_id)}
          >
            <TrashIcon size={16} />
          </DcButton>
        ) : (
          <span>-</span>
        ),
    },
  ]

  const exportRequestToExcel = () => {
    if (!request) return

    const productsData = (lineItems || []).map((item) => ({
      Name: item.product.product_name,
      SKU: item.product.product_sku,
      Price: item.product.sale_price ?? '',
      Quantity: item.quantity,
    }))

    const productsSheet = XLSX.utils.json_to_sheet(productsData)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, productsSheet, 'Products')

    const fileName = `warehouse_transfer_${request.id}.xlsx`
    XLSX.writeFile(wb, fileName)
  }

  return (
    <DcFlexBox flexDirection="column" gap="20px">
      {currentStatus === 'draft' && (
        <SectionContent>
          <DcFlexBox justifyContent="flex-end" height="auto" gap="10px">
            <DcButton
              size="sm"
              variant="outline-success"
              onClick={() => handleApprove(request?.id)}
              withIcon={true}
            >
              <CheckIcon size={16} />
              <span>Approve</span>
            </DcButton>
            <DcButton
              size="sm"
              variant="outline-danger"
              onClick={() => handleReject(request?.id)}
              withIcon={true}
            >
              <XmarkIcon size={16} />
              <span>Reject</span>
            </DcButton>
          </DcFlexBox>
        </SectionContent>
      )}
      <DcFlexBox flexDirection="column" gap="20px" padding="20px">
        {/* Modern Summary Card UI */}
        {/* //add here aapprove and reject button */}

        <div
          style={{
            background: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #e9ecef',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
            padding: '20px',
            marginBottom: '10px',
          }}
        >
          {/* Top Row: Title/meta left, status right */}
          <DcFlexBox
            height="65px"
            justifyContent="space-between"
            alignItems="flex-start"
            backgroundColor={colors.grayLighter}
            padding="10px"
            style={{ marginBottom: 16 }}
          >
            <DcFlexBox height="auto" flexDirection="column" gap="2px">
              <PixelText
                textSize="16px"
                style={{ fontWeight: 600, color: '#495057' }}
              >
                Inventory Request Details
              </PixelText>
              <PixelText textSize="13px" style={{ color: '#6c757d' }}>
                ID: {request?.id} &nbsp;•&nbsp;{' '}
                {formatDate(request?.created_at?.date)}
              </PixelText>
            </DcFlexBox>
            <DcTag
              style={{
                backgroundColor: statusColors.bg,
                color: statusColors.text,
                padding: '4px 16px',
                borderRadius: '20px',
                fontSize: '13px',
                fontWeight: 500,
              }}
            >
              {currentStatus?.charAt(0).toUpperCase() + currentStatus?.slice(1)}
            </DcTag>
          </DcFlexBox>
          {/* First Row: Warehouse, Products, Quantity, Date */}
          <DcFlexBox height="65px" gap="40px" style={{ marginBottom: 12 }}>
            <DcFlexBox
              height="auto"
              alignItems="center"
              gap="8px"
              style={{ minWidth: 180 }}
            >
              <WarehouseIcon size={20} color="#2563eb" />
              <div>
                <PixelText textSize="12px" style={{ color: '#6c757d' }}>
                  Source Warehouse
                </PixelText>
                <PixelText textSize="14px" style={{ fontWeight: 500 }}>
                  {request?.source_warehouse?.name}
                </PixelText>
              </div>
            </DcFlexBox>
            <DcFlexBox alignItems="center" gap="8px" style={{ minWidth: 180 }}>
              <WarehouseIcon size={20} color="#2563eb" />
              <div>
                <PixelText textSize="12px" style={{ color: '#6c757d' }}>
                  Destination Location
                </PixelText>
                <PixelText textSize="14px" style={{ fontWeight: 500 }}>
                  {request?.destination_warehouse?.name}
                </PixelText>
              </div>
            </DcFlexBox>
            <DcFlexBox
              height="auto"
              alignItems="center"
              gap="8px"
              style={{ minWidth: 120 }}
            >
              <ProductCatalogIcon size={20} color="#2563eb" />
              <div>
                <PixelText textSize="12px" style={{ color: '#6c757d' }}>
                  Products
                </PixelText>
                <PixelText textSize="14px" style={{ fontWeight: 500 }}>
                  {request?.total_products}
                </PixelText>
              </div>
            </DcFlexBox>
            <DcFlexBox
              height="auto"
              alignItems="center"
              gap="8px"
              style={{ minWidth: 120 }}
            >
              <ProductCatalogIcon size={20} color="#2563eb" />
              <div>
                <PixelText textSize="12px" style={{ color: '#6c757d' }}>
                  Items
                </PixelText>
                <PixelText textSize="14px" style={{ fontWeight: 500 }}>
                  {request?.total_quantity}
                </PixelText>
              </div>
            </DcFlexBox>
          </DcFlexBox>
          {/* Second Row: Products, Quantity, Notes */}
          <DcFlexBox
            padding="10px"
            backgroundColor={colors.grayLighter}
            height="65px"
            gap="40px"
          >
            <DcFlexBox alignItems="center" gap="8px" style={{ minWidth: 120 }}>
              <CalendarIcon size={20} color="#2563eb" />
              <div>
                <PixelText textSize="12px" style={{ color: '#6c757d' }}>
                  Created by
                </PixelText>
                <PixelText textSize="14px" style={{ fontWeight: 500 }}>
                  {request?.created_by_user}
                </PixelText>
              </div>
            </DcFlexBox>
            <DcFlexBox alignItems="center" gap="8px" style={{ minWidth: 120 }}>
              <ReferenceIcon size={20} color="#2563eb" />
              <div>
                <PixelText textSize="12px" style={{ color: '#6c757d' }}>
                  Reference ID
                </PixelText>
                <PixelText textSize="14px" style={{ fontWeight: 500 }}>
                  {request?.reference_id}
                </PixelText>
              </div>
            </DcFlexBox>
            {/* {request?.request_notes && ( */}
            <DcFlexBox alignItems="center" gap="8px" style={{ minWidth: 200 }}>
              <StickyNoteIcon size={20} color="#2563eb" />
              <div>
                <PixelText textSize="12px" style={{ color: '#6c757d' }}>
                  Notes
                </PixelText>
                <PixelText textSize="14px" style={{ fontWeight: 500 }}>
                  {request?.request_notes || '--'}
                </PixelText>
              </div>
            </DcFlexBox>
            <DcFlexBox alignItems="center" gap="8px" style={{ minWidth: 200 }}>
              {request?.transfer_type == 'warehouse' ? (
                <WarehouseIcon size={20} />
              ) : (
                <MarketplaceStallIcon color={colors.info} size={20} />
              )}
              <div>
                <PixelText textSize="12px" style={{ color: '#6c757d' }}>
                  Transfer To:
                </PixelText>
                <PixelText textSize="14px" style={{ fontWeight: 500 }}>
                  <DcTag
                    textColor={
                      request?.transfer_type == 'warehouse'
                        ? colors.secondary
                        : colors.warning
                    }
                    customBackgroundColor={
                      request?.transfer_type == 'warehouse'
                        ? colors.secondaryLight
                        : colors.warningLight
                    }
                  >
                    {request?.transfer_type || '--'}
                  </DcTag>
                </PixelText>
              </div>
            </DcFlexBox>
            {/* )} */}
          </DcFlexBox>
        </div>
        {/* Products Table (unchanged) */}
        <DcFlexBox
          height="auto"
          style={{
            background: '#fff',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #e9ecef',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          }}
          flexDirection="column"
          gap="10px"
        >
          <DcFlexBox
            flexDirection="row"
            justifyContent="space-between"
            gap="10px"
          >
            <DcFlexBox alignItems="center" gap="10px">
              <PixelText
                textSize="18px"
                style={{
                  fontWeight: '600',
                  color: '#495057',
                }}
              >
                Products ({lineItems.length})
              </PixelText>
            </DcFlexBox>

            {!edit ? (
              <DcFlexBox width="fit-content" height="auto" gap="5px">
                <DcDiv width="260px">
                  <DcInput
                    inputSize="small"
                    placeholder="Search products..."
                    value={productSearch}
                    showsearchicon={1}
                    onChange={(e: any) => setProductSearch(e.target.value)}
                    name="productSearch"
                  />
                </DcDiv>
                {currentStatus !== 'cancelled' &&
                  currentStatus !== 'completed' && (
                    <DcButton
                      width="fit-content"
                      variant="outline-success"
                      size="sm"
                      withIcon={true}
                      onClick={() => {
                        setData(
                          (lineItems || []).map((li) => ({
                            product_id: li.product.product_id,
                            quantity: li.quantity,
                          })),
                        )
                        setEdit(true)
                      }}
                    >
                      <PencilIcon size={16} />
                      <span>Edit</span>
                    </DcButton>
                  )}
                <DcButton
                  width="fit-content"
                  variant="outline-secondary"
                  size="sm"
                  withIcon={true}
                  onClick={exportRequestToExcel}
                >
                  <ArrowRightFromBracketIcon size={16} />
                  <span>Export</span>
                </DcButton>
              </DcFlexBox>
            ) : (
              <DcFlexBox width="fit-content" height="auto" gap="5px">
                <DcButton
                  width="fit-content"
                  variant="outline-secondary"
                  size="sm"
                  withIcon={true}
                  onClick={() => {
                    setEdit(false)
                    setData(null)
                  }}
                >
                  <XmarkIcon size={16} />
                  Cancel
                </DcButton>
                <DcButton
                  width="fit-content"
                  variant="outline-success"
                  size="sm"
                  withIcon={true}
                  onClick={() => {
                    handleEditProducts()
                  }}
                >
                  <CheckIcon size={16} />
                  <span>Save</span>
                </DcButton>
              </DcFlexBox>
            )}
          </DcFlexBox>
          {(() => {
            const displayedLineItems = React.useMemo(() => {
              // In edit mode, always reflect `data`, even if empty (e.g., after deletes)
              const baseItems = (() => {
                if (!edit || data == null) return lineItems
                const desiredIds = new Set(data.map((d) => d.product_id))
                return (lineItems || [])
                  .filter((li) => desiredIds.has(li.product.product_id))
                  .map((li) => {
                    const override = data.find(
                      (d) => d.product_id === li.product.product_id,
                    )
                    return override
                      ? { ...li, quantity: override.quantity }
                      : li
                  })
              })()

              const term = (productSearch || '').trim().toLowerCase()
              if (!term) return baseItems

              return (baseItems || []).filter((li) => {
                const name = (li.product.product_name || '').toLowerCase()
                const sku = (li.product.product_sku || '').toLowerCase()
                const qty = String(li.quantity || '')
                const price = String(
                  li.product.sale_price != null ? li.product.sale_price : '',
                ).toLowerCase()
                return (
                  name.includes(term) ||
                  sku.includes(term) ||
                  qty.includes(term) ||
                  price.includes(term)
                )
              })
            }, [edit, data, lineItems, productSearch])

            return (
              <PixelTable
                columns={columns}
                data={displayedLineItems}
                pagination={false}
                fixedHeader={true}
                fixedHeaderScrollHeight="calc(100vh - 560px)"
                onRowClicked={(row: IInventoryRequestLineItem) => {
                  setSelectedProductId(row.product.product_id)
                  setShowProductDetail(true)
                }}
              />
            )
          })()}
        </DcFlexBox>
        {/* Product Detail Slider */}
        <DcOverlaySlider
          isOpen={showProductDetail}
          onClose={() => setShowProductDetail(false)}
          title="Product Details"
          width="60%"
        >
          {selectedProductId && (
            <ProductDetail productId={selectedProductId} setEditId={() => {}} />
          )}
        </DcOverlaySlider>
      </DcFlexBox>
    </DcFlexBox>
  )
}

interface IInventoryRequest {
  id: number
  ems_customer_id: number
  reference_id: string
  source_warehouse_id: {
    wh_id: number
    id: number
    name: string
    warehouse_code: string
    is_default: boolean | null
    address: string
    description: string
    city: string
    zip: string | null
    province: string | null
    country: string | null
    phone: string
    active: boolean
    created_at: {
      date: string
      timezone_type: number
      timezone: string
    }
  }
  destination_warehouse_id: {
    wh_id: number
    id: number
    name: string
    warehouse_code: string
    is_default: boolean | null
    address: string
    description: string
    city: string
    zip: string | null
    province: string | null
    country: string | null
    phone: string
    active: boolean
    created_at: {
      date: string
      timezone_type: number
      timezone: string
    }
  }
  request_status: string
  total_products: number
  total_quantity: number
  request_notes: string | null
  created_at: {
    date: string
    timezone_type: number
    timezone: string
  }
  updated_at: string | null
}

const breadcrumbs = [
  {
    name: 'Locations',
    active: false,
  },
  {
    name: 'Inventory Request',
    active: true,
  },
]

const WarehouseTransferRequest = () => {
  const [inventoryRequests, setInventoryRequests] = React.useState<
    IInventoryRequest[]
  >([])
  const [tableLoading, setTableLoading] = React.useState(true)
  const [showNewModal, setShowNewModal] = React.useState(false)
  const [selectedRequest, setSelectedRequest] =
    React.useState<IInventoryRequest | null>(null)
  const [statusFilter, setStatusFilter] = React.useState<string>('all')

  const [showDetailModal, setShowDetailModal] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const [detailLineItems, setDetailLineItems] = useState<
    IInventoryRequestLineItem[]
  >([])
  const getInventoryRequests = () => {
    setTableLoading(true)
    const statusParam = statusFilter === 'all' ? '' : `?status=${statusFilter}`
    get(`/warehouse/transfer${statusParam}`).subscribe((res: any) => {
      const requests = res.response.data?.map((request: any) => ({
        ...request,
        expandDisabled: true,
      }))
      setInventoryRequests(requests || [])
      setTableLoading(false)
    })
  }
  const updateStatus = (requestId: number, status: string) => {
    setInventoryRequests(
      inventoryRequests.map((request) =>
        request.id === requestId
          ? { ...request, request_status: status }
          : request,
      ),
    )
  }

  React.useEffect(() => {
    getInventoryRequests()
  }, [statusFilter])

  const columns = [
    {
      dataKey: 'id',
      name: <PixelText multiLine>ID</PixelText>,
      width: '5%',
      sortable: true,
      selector: (row: IInventoryRequest) => (row.id ? row.id : '--'),
    },
    {
      dataKey: 'created_at',
      name: <PixelText multiLine>Creation Date</PixelText>,
      // width: '10%',
      sortable: true,
      selector: (row: IInventoryRequest) => {
        return row.created_at?.date ? (
          <PixelDate
            value={row.created_at.date}
            format="dateWithTime"
            className=""
          />
        ) : (
          '--'
        )
      },
    },
    {
      dataKey: 'source_warehouse_name',
      name: <PixelText multiLine>Source Warehouse</PixelText>,
      sortable: true,
      selector: (row: IInventoryRequest) =>
        row.source_warehouse?.name ? row.source_warehouse.name : '--',
    },
    {
      dataKey: 'destination_warehouse_name',
      name: <PixelText multiLine>Destination Location</PixelText>,
      sortable: true,
      selector: (row: IInventoryRequest) =>
        row.destination_warehouse?.name ? row.destination_warehouse.name : '--',
    },
    {
      dataKey: 'transfer_type',
      name: <PixelText multiLine>Transfer To</PixelText>,
      sortable: true,
      selector: (row: IInventoryRequest) => (
        //SHOW INPIXELTAG
        <DcTag
          customBackgroundColor={
            row.transfer_type == 'warehouse'
              ? colors.secondaryLight
              : row.transfer_type == 'marketplace'
              ? colors.warningLight
              : colors.grayLight
          }
          textColor={
            row.transfer_type == 'warehouse' ? colors.secondary : colors.warning
          }
        >
          {row.transfer_type ? row.transfer_type : '--'}{' '}
        </DcTag>
      ),
    },
    {
      dataKey: 'total_products',
      name: <PixelText multiLine>Products</PixelText>,
      width: '7%',
      sortable: true,
      selector: (row: IInventoryRequest) =>
        row.total_products ? row.total_products : '--',
    },
    {
      dataKey: 'total_quantity',
      name: <PixelText multiLine>Quantity</PixelText>,
      width: '7%',
      sortable: true,
      selector: (row: IInventoryRequest) =>
        row.total_quantity ? row.total_quantity : '--',
    },

    {
      dataKey: 'request_status',
      name: <PixelText multiLine>Status</PixelText>,
      width: '7%',
      sortable: true,
      selector: (row: IInventoryRequest) => {
        const status = row.request_status || 'draft'
        const statusColors: Record<string, string> = {
          draft: colors.info,
          completed: colors.success,
          cancelled: colors.danger,
        }
        const statusBgColors: Record<string, string> = {
          draft: colors.infoLight,
          completed: colors.successLight,
          cancelled: colors.dangerLight,
        }
        return (
          <DcTag
            textColor={statusColors[status] || colors.info}
            customBackgroundColor={statusBgColors[status] || colors.infoLight}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </DcTag>
        )
      },
    },

    {
      dataKey: 'request_notes',
      name: <PixelText multiLine>Notes</PixelText>,
      width: '10%',
      sortable: true,
      selector: (row: IInventoryRequest) =>
        row.request_notes ? row.request_notes : '--',
    },
  ]

  const handleRowClick = (row: IInventoryRequest) => {
    setSelectedRequest(row)
    setShowDetailModal(true)
    setLoading(true)
    get(`/warehouse/transfer/${row.id}`).subscribe((res: any) => {
      if (res.response.data) {
        // Just set the line items data, header comes from the clicked row
        setDetailLineItems(res.response.data)
        setLoading(false)
      } else {
        setDetailLineItems([])
        setLoading(false)
      }
    })
  }

  const reloadDetails = (requestId: number) => {
    get(`/warehouse/transfer/${requestId}`).subscribe((res: any) => {
      if (res.response.data) {
        setDetailLineItems(res.response.data)
      } else {
        setDetailLineItems([])
      }
    })
  }

  return (
    <>
      <SectionContent
        style={{
          borderBottom: '1px solid #e0e0e0',
          borderRadius: '0px',
          padding: '5px 0px',
        }}
      >
        <DcFlexBox
          justifyContent="space-between"
          alignItems="center"
          padding="0px 20px"
        >
          <DcBreadcrumbs data={breadcrumbs} />
          <ProfileSection />
        </DcFlexBox>
      </SectionContent>
      <PixelPageContainer
        style={{ minHeight: 'calc(100vh - 65px)' }}
        padding="0px"
      >
        <SectionContent style={{ padding: '5px 20px', borderRadius: '0px' }}>
          <DcFlexBox
            justifyContent="flex-end"
            height="auto"
            padding="0px"
            gap="5px"
          >
            <DcDiv width="250px">
              <DcDropDown
                width={'150px'}
                dropdownSize="sm"
                options={[
                  { value: 'all', label: 'All Status' },
                  { value: 'draft', label: 'Draft' },
                  { value: 'completed', label: 'Completed' },
                  { value: 'cancelled', label: 'Cancelled' },
                ]}
                value={statusFilter}
                onChange={(e: any) => setStatusFilter(e.target.value)}
                customLabel="Filter by Status"
                placeholder="Select Status"
              />
            </DcDiv>
            <DcDiv width="130px">
              <DcButton
                withIcon={true}
                size="sm"
                onClick={() => setShowNewModal(true)}
              >
                <PlusIcon size={16} />
                <span>New Request</span>
              </DcButton>
            </DcDiv>
          </DcFlexBox>
        </SectionContent>
        <Section
          style={{
            padding: '20px',
            height: 'calc(100vh - 95px)',
            overflow: 'auto',
          }}
        >
          <SectionContent style={{ margin: '20px 0px 0px 0px' }}>
            <PixelTable
              columns={columns}
              data={inventoryRequests}
              fixedHeader
              fixedHeaderScrollHeight="calc(100vh - 285px)"
              progressPending={tableLoading}
              onRowClicked={handleRowClick}
              pagination
              isSearchable
            />
          </SectionContent>
        </Section>

        <DcOverlaySlider
          isOpen={showNewModal}
          onClose={() => {
            setShowNewModal(false)
            setSelectedRequest(null)
          }}
          title={selectedRequest ? 'Request Details' : 'New Inventory Request'}
          width="70%"
        >
          {selectedRequest ? (
            <div style={{ padding: '20px' }}>
              <div>
                <h3>Request Details</h3>
                <p>
                  <strong>Reference ID:</strong> {selectedRequest.reference_id}
                </p>
                <p>
                  <strong>Source Warehouse:</strong>{' '}
                  {selectedRequest.source_warehouse_id?.name}
                </p>
                <p>
                  <strong>Destination Warehouse:</strong>{' '}
                  {selectedRequest.destination_warehouse_id?.name}
                </p>
                <p>
                  <strong>Status:</strong> {selectedRequest.request_status}
                </p>

                <p>
                  <strong>Total Products:</strong>{' '}
                  {selectedRequest.total_products}
                </p>
                <p>
                  <strong>Total Quantity:</strong>{' '}
                  {selectedRequest.total_quantity}
                </p>
                <p>
                  <strong>Created:</strong>{' '}
                  {selectedRequest.created_at?.date
                    ? new Date(selectedRequest.created_at.date).toLocaleString()
                    : '--'}
                </p>
                {selectedRequest.request_notes && (
                  <p>
                    <strong>Notes:</strong> {selectedRequest.request_notes}
                  </p>
                )}
                {selectedRequest.transfer_type && (
                  <p>
                    <strong>Transfer Type:</strong>{' '}
                    {selectedRequest.transfer_type}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <InventoryRequestForm
              onClose={() => {
                setShowNewModal(false)
                setSelectedRequest(null)
              }}
              onSuccess={() => {
                getInventoryRequests()
              }}
            />
          )}
        </DcOverlaySlider>

        {/* Detail Modal */}
        <DcOverlaySlider
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false)
            setSelectedRequest(null)
            setDetailLineItems([])
          }}
          title="Inventory Request Details"
          width="70%"
          loading={loading}
          padding="0px"
        >
          {selectedRequest && (
            <InventoryRequestDetailContent
              lineItems={detailLineItems}
              request={selectedRequest}
              updateStatus={updateStatus}
              reloadDetails={reloadDetails}
              Close={() => {
                setShowDetailModal(false)
                setSelectedRequest(null)
                setDetailLineItems([])
              }}
            />
          )}
        </DcOverlaySlider>
      </PixelPageContainer>
    </>
  )
}

export default WarehouseTransferRequest
